/* One calendar for product delivery dates. Numeric ranges are rendered by Liquid
   from shop.metafields.custom.delivery_settings; this file contains no tier ranges. */
(() => {
  const DAY = 86400000;
  const holidaysByYear = new Map();
  const key = (date) => date.toISOString().slice(0, 10);
  const civilDate = (year, month, day) => new Date(Date.UTC(year, month, day, 12));

  function holidaysForYear(year) {
    if (holidaysByYear.has(year)) return holidaysByYear.get(year);
    const holidays = new Set();
    // Neighboring years include New Year's Day observed on December 31.
    for (let y = year - 1; y <= year + 1; y += 1) {
      [[0, 1], [5, 19], [6, 4], [10, 11], [11, 25]].forEach(([month, day]) => {
        const holiday = civilDate(y, month, day);
        holidays.add(key(holiday));
        if (holiday.getUTCDay() === 6) holiday.setUTCDate(holiday.getUTCDate() - 1);
        else if (holiday.getUTCDay() === 0) holiday.setUTCDate(holiday.getUTCDate() + 1);
        holidays.add(key(holiday));
      });
      [[0, 1, 3], [1, 1, 3], [8, 1, 1], [9, 1, 2], [10, 4, 4]].forEach(([month, weekday, occurrence]) => {
        const first = civilDate(y, month, 1);
        holidays.add(key(civilDate(y, month, 1 + (weekday - first.getUTCDay() + 7) % 7 + (occurrence - 1) * 7)));
      });
      const memorial = civilDate(y, 4, 31);
      memorial.setUTCDate(31 - (memorial.getUTCDay() + 6) % 7);
      holidays.add(key(memorial));
    }
    holidaysByYear.set(year, holidays);
    return holidays;
  }

  const isBusinessDay = (date) => date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !holidaysForYear(date.getUTCFullYear()).has(key(date));

  function addBusinessDays(start, count) {
    if (!Number.isInteger(count) || count < 0 || count > 365) throw new RangeError('Invalid business-day range');
    const result = new Date(start);
    let added = 0;
    while (added < count) {
      result.setTime(result.getTime() + DAY);
      if (isBusinessDay(result)) added += 1;
    }
    return result;
  }

  function calculateRange(now, settings) {
    const { processingMin, processingMax, transitMin, transitMax, timezone, cutoffHour, cutoffMinute } = settings;
    if (![processingMin, processingMax, transitMin, transitMax, cutoffHour, cutoffMinute].every(Number.isInteger) ||
        processingMin < 0 || processingMax < processingMin || transitMin < 0 || transitMax < transitMin ||
        processingMax + transitMax > 365 || cutoffHour < 0 || cutoffHour > 23 || cutoffMinute < 0 || cutoffMinute > 59 || !timezone) {
      throw new RangeError('Invalid delivery settings');
    }
    // Convert the instant to the merchant's civil date, then use UTC arithmetic.
    // Customer timezone and daylight-saving changes cannot shift a delivery day.
    const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
      timeZone: timezone, year: 'numeric', month: 'numeric', day: 'numeric',
      hour: 'numeric', minute: 'numeric', hourCycle: 'h23'
    }).formatToParts(now).map(({ type, value }) => [type, value]));
    let anchor = civilDate(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
    const afterCutoff = Number(parts.hour) * 60 + Number(parts.minute) > cutoffHour * 60 + cutoffMinute;
    // Orders outside fulfillment days or after cutoff start on the next
    // business day (day zero), consistent with the shipping-service schedule.
    if (!isBusinessDay(anchor) || afterCutoff) anchor = addBusinessDays(anchor, 1);
    return {
      earliest: addBusinessDays(anchor, processingMin + transitMin),
      latest: addBusinessDays(anchor, processingMax + transitMax)
    };
  }

  // Expose only to the local Node test runner, not to the storefront global scope.
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculateRange, addBusinessDays, isBusinessDay };
  if (typeof customElements === 'undefined' || customElements.get('ps-delivery')) return;

  customElements.define('ps-delivery', class extends HTMLElement {
    connectedCallback() {
      this.updateRange();
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          const productInfo = this.closest('product-info');
          if (!productInfo || data.sectionId !== (productInfo.dataset.originalSection || productInfo.dataset.section)) return;
          const source = Array.from(data.html.querySelectorAll('ps-delivery')).find((element) =>
            element.dataset.deliveryKind === this.dataset.deliveryKind && element.dataset.productId === this.dataset.productId
          );
          if (source) this.replaceWith(source.cloneNode(true));
        });
      }
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      this.unsubscribe = undefined;
    }

    updateRange() {
      const range = this.querySelector('[data-ps-delivery-range]');
      if (!range || !this.hasAttribute('data-processing-min')) return;
      try {
        const dates = calculateRange(new Date(), {
          processingMin: Number(this.dataset.processingMin), processingMax: Number(this.dataset.processingMax),
          transitMin: Number(this.dataset.transitMin), transitMax: Number(this.dataset.transitMax),
          timezone: this.dataset.timezone, cutoffHour: Number(this.dataset.cutoffHour), cutoffMinute: Number(this.dataset.cutoffMinute)
        });
        const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
        range.textContent = `${formatter.format(dates.earliest)} – ${formatter.format(dates.latest)}`;
      } catch (error) {
        // Keep the complete server-rendered business-day estimate if date enhancement fails.
      }
    }
  });
})();
